import React, { useEffect, useState } from "react";
import { ButtonGroup, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "../components";
import { CheckDataRetention, SubmitDataRetentionChoice } from "../utilities/api/user/user-service";
import { LogoutUser } from "../utilities";
import { resetVerifyUserSlice } from "../redux/Slice/Auth/VerifyUserSlice";
import { resetProfileDetailSlice } from "../redux/Slice/Profile/ProfileDetailSlice";

const DataRetentionProvider = () => {
    const [showRetentionPopup, setShowRetentionPopup] = useState(false);
    const [confirmChoice, setConfirmChoice] = useState<"keep" | "delete" | null>(null);
    const [retentionLoading, setRetentionLoading] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        CheckDataRetention()?.then((res: any) => {
            if (res?.data?.showRetentionPopup) {
                setShowRetentionPopup(true);
            }
        });
    }, []);

    const handleRetentionChoice = async (choice: "keep" | "delete") => {
        setRetentionLoading(true);
        await SubmitDataRetentionChoice(choice);
        setRetentionLoading(false);
        setShowRetentionPopup(false);
        setConfirmChoice(null);

        if (choice === "delete") {
            await LogoutUser();
            dispatch(resetVerifyUserSlice());
            dispatch(resetProfileDetailSlice());
            localStorage.removeItem("device-uuid");
            localStorage.removeItem("authToken");
            localStorage.removeItem("device-token");
            navigate("/");
        }
    };

    return (
        <>
            {/* Step 1 — Choice Modal */}
            <Modal
                show={showRetentionPopup && !confirmChoice}
                onHide={() => {}}
                aria-labelledby="data-retention-modal"
                centered
                backdrop="static"
                keyboard={false}
                className="regenerate-password"
            >
                <Modal.Header className="border-0 pb-0 pb-2">
                    <Modal.Title id="data-retention-modal" className="fw-semibold">
                        Data Retention Notice
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Your account data is <strong>3 years old</strong>. As per our data retention policy, you can choose to keep or delete your data.</p>
                    <p className="text-muted">If you choose <strong>Delete</strong>, your account will be permanently removed.</p>
                    <div className="text-end">
                        <ButtonGroup>
                            <Button
                                type="button"
                                bname="Keep My Data"
                                name="keep"
                                id="keep"
                                onClick={() => setConfirmChoice("keep")}
                                className="btn btn-light rounded-1 mt-1 me-3"
                            />
                            <Button
                                type="button"
                                bname="Delete My Data"
                                name="delete"
                                id="delete"
                                onClick={() => setConfirmChoice("delete")}
                                className="btn btn-danger rounded-1 mt-1"
                            />
                        </ButtonGroup>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Step 2 — Confirmation Modal */}
            <Modal
                show={!!confirmChoice}
                onHide={() => setConfirmChoice(null)}
                aria-labelledby="data-retention-confirm-modal"
                centered
                backdrop="static"
                keyboard={false}
                className="regenerate-password"
            >
                <Modal.Header className="border-0 pb-0 pb-2" closeButton>
                    <Modal.Title id="data-retention-confirm-modal" className="fw-semibold">
                        Confirmation
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to{" "}
                        <strong className={confirmChoice === "delete" ? "text-danger" : ""}>
                            {confirmChoice === "keep" ? "keep your data" : "permanently delete your account"}
                        </strong>?
                    </p>
                    {confirmChoice === "delete" && (
                        <p className="text-danger">This action cannot be undone.</p>
                    )}
                    <div className="text-end">
                        <ButtonGroup>
                            <Button
                                type="button"
                                bname="No, Go Back"
                                name="back"
                                id="back"
                                disabled={retentionLoading}
                                onClick={() => setConfirmChoice(null)}
                                className="btn btn-light rounded-1 mt-1 me-3"
                            />
                            <Button
                                type="button"
                                bname={retentionLoading ? "Please wait..." : "Yes, Confirm"}
                                name="confirm"
                                id="confirm"
                                disabled={retentionLoading}
                                onClick={() => handleRetentionChoice(confirmChoice!)}
                                className={`btn rounded-1 mt-1 ${confirmChoice === "delete" ? "btn-danger" : "btn-primary"}`}
                            />
                        </ButtonGroup>
                    </div>
                </Modal.Body>
            </Modal>

        </>
    );
};

export { DataRetentionProvider };
